'use client';

import * as React from 'react';

import { cn } from '@/utils/cn';

import type { DefaultLegendContentProps, TooltipContentProps } from 'recharts';
import * as RechartsPrimitive from 'recharts';
import type {
  NameType,
  ValueType
} from 'recharts/types/component/DefaultTooltipContent';

// Format: { THEME_NAME: CSS_SELECTOR }
// Use explicit selectors that work when `dark` is applied to `html` and
// fallback to `:root` for the light theme.
const THEMES = { light: ':root', dark: 'html.dark' } as const;

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  );
};

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);

  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />');
  }

  return context;
}

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    config: ChartConfig;
    children: React.ComponentProps<
      typeof RechartsPrimitive.ResponsiveContainer
    >['children'];
  }
>(({ id, className, children, config, ...props }, ref) => {
  const uniqueId = React.useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`;

  // Let the parent control layout. We set height:100% so the wrapper fills
  // the parent's content box (CardContent, etc). Recharts' ResponsiveContainer
  // will then use percentage height and size correctly. However, many parent
  // containers don't provide an explicit height which makes Recharts warn
  // about width/height <= 0. Provide a sensible minHeight fallback unless the
  // caller already provided one via `style`.
  const incomingStyle = (props as any).style || {};
  const wrapperStyle: React.CSSProperties = {
    display: 'block',
    minWidth: 0,
    height: '100%',
    width: '100%',
    // If caller didn't set minHeight, provide a fallback so ResponsiveContainer
    // can compute sizes (avoids "width(-1) and height(-1)" warnings).
    minHeight: incomingStyle.minHeight ?? 250,
    ...incomingStyle
  };

  // Track whether the wrapper has a measurable size. Recharts' ResponsiveContainer
  // logs warnings when width/height are zero or negative; using a ResizeObserver
  // we only render the ResponsiveContainer once the wrapper has a positive
  // width and height.
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const [hasSize, setHasSize] = React.useState(false);
  const [measuredHeight, setMeasuredHeight] = React.useState<number | null>(
    null
  );

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const node = wrapperRef.current;
    if (!node) return;

    const measure = () => {
      try {
        const rect = node.getBoundingClientRect();
        const fallback = (wrapperStyle.minHeight as number) || 250;

        if (rect.width > 0 && rect.height > 0) {
          setHasSize(true);
          // Use the measured height but ensure it's at least the fallback.
          setMeasuredHeight(Math.max(fallback, Math.floor(rect.height)));
        } else {
          // If the element currently reports zero height (e.g. parent uses
          // percentage height without an explicit pixel basis), use the
          // minHeight fallback so Recharts still receives a valid pixel
          // height and can render.
          setHasSize(true);
          setMeasuredHeight(fallback);
        }
      } catch (e) {
        // On any error, fall back to a safe pixel height so charts render.
        setHasSize(true);
        setMeasuredHeight((wrapperStyle.minHeight as number) || 250);
      }
    };

    measure();

    const ro = new ResizeObserver(() => {
      measure();
    });

    ro.observe(node);

    return () => ro.disconnect();
  }, []);

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        ref={(node) => {
          // attach both forwarded ref and local ref
          (ref as any) = (ref as any) || null;
          try {
            if (typeof ref === 'function') ref(node);
            else if (ref && 'current' in (ref as any))
              (ref as any).current = node;
          } catch (e) {
            // ignore
          }
          wrapperRef.current = node;
        }}
        style={wrapperStyle}
        className={cn(
          "[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border flex justify-center text-xs [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-sector]:outline-none [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-none",
          className
        )}
        {...props}>
        <ChartStyle
          id={chartId}
          config={config}
        />
        {hasSize && measuredHeight ? (
          // Pass a concrete pixel height to ResponsiveContainer to avoid
          // percentage-relative calculations that sometimes resolve to 0.
          <RechartsPrimitive.ResponsiveContainer
            width='100%'
            height={measuredHeight}>
            {children}
          </RechartsPrimitive.ResponsiveContainer>
        ) : (
          // Render an invisible placeholder to avoid Recharts warnings while
          // the container stabilizes size.
          <div
            style={{
              width: '100%',
              height: '100%',
              minHeight: wrapperStyle.minHeight
            }}
          />
        )}
      </div>
    </ChartContext.Provider>
  );
});
ChartContainer.displayName = 'Chart';

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(
    ([_, config]) => config.theme || config.color
  );

  if (!colorConfig.length) {
    return null;
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(([theme, prefix]) => {
            return `${prefix} [data-chart="${id}"] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color =
      itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ||
      itemConfig.color;
    return color ? `  --color-${key}: ${color};` : null;
  })
  .filter(Boolean)
  .join('\n')}
}`;
          })
          .join('\n')
      }}
    />
  );
};

const ChartTooltip = RechartsPrimitive.Tooltip;

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  TooltipContentProps<ValueType, NameType> &
    Omit<React.ComponentProps<'div'>, 'content'> & {
      hideLabel?: boolean;
      hideIndicator?: boolean;
      indicator?: 'line' | 'dot' | 'dashed';
      nameKey?: string;
      labelKey?: string;
    }
>(
  (
    {
      active,
      payload,
      className,
      indicator = 'dot',
      hideLabel = false,
      hideIndicator = false,
      label,
      labelFormatter,
      labelClassName,
      formatter,
      color,
      nameKey,
      labelKey
    },
    ref
  ) => {
    const { config } = useChart();

    const tooltipLabel = React.useMemo(() => {
      if (hideLabel || !payload?.length) {
        return null;
      }

      const [item] = payload;
      const key = `${labelKey || item.dataKey || item.name || 'value'}`;
      const itemConfig = getPayloadConfigFromPayload(config, item, key);
      const value =
        !labelKey && typeof label === 'string'
          ? config[label as keyof typeof config]?.label || label
          : itemConfig?.label;

      if (labelFormatter) {
        return (
          <div className={cn('font-medium', labelClassName)}>
            {labelFormatter(value, payload)}
          </div>
        );
      }

      if (!value) {
        return null;
      }

      return <div className={cn('font-medium', labelClassName)}>{value}</div>;
    }, [
      label,
      labelFormatter,
      payload,
      hideLabel,
      labelClassName,
      config,
      labelKey
    ]);

    if (!active || !payload?.length) {
      return null;
    }

    const nestLabel = payload.length === 1 && indicator !== 'dot';

    return (
      <div
        ref={ref}
        className={cn(
          'border-border/50 bg-card grid min-w-32 items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl',
          className
        )}>
        {!nestLabel ? tooltipLabel : null}
        <div className='grid gap-1.5'>
          {payload.map((item, index) => {
            const key = `${nameKey || item.name || item.dataKey || 'value'}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);
            const indicatorColor = color || item.payload?.fill || item.color;

            return (
              <div
                key={key}
                className={cn(
                  '[&>svg]:text-muted-foreground flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5',
                  indicator === 'dot' && 'items-center'
                )}>
                {formatter && item?.value !== undefined && item.name ? (
                  formatter(item.value, item.name, item, index, item.payload)
                ) : (
                  <>
                    {itemConfig?.icon ? (
                      <itemConfig.icon />
                    ) : (
                      !hideIndicator && (
                        <div
                          className={cn(
                            'shrink-0 rounded-[2px] border-[var(--color-border)] bg-[var(--color-bg)]',
                            {
                              'size-2.5': indicator === 'dot',
                              'w-1': indicator === 'line',
                              'w-0 border-[1.5px] border-dashed bg-transparent':
                                indicator === 'dashed',
                              'my-0.5': nestLabel && indicator === 'dashed'
                            }
                          )}
                          style={
                            {
                              '--color-bg': `var(--color-${key}, ${indicatorColor ?? 'transparent'})`,
                              '--color-border': `var(--color-${key}, ${indicatorColor ?? 'transparent'})`
                            } as React.CSSProperties
                          }
                        />
                      )
                    )}
                    <div
                      className={cn(
                        'flex flex-1 justify-between leading-none',
                        nestLabel ? 'items-end' : 'items-center'
                      )}>
                      <div className='grid gap-1.5'>
                        {nestLabel ? tooltipLabel : null}
                        <span className='text-muted-foreground'>
                          {itemConfig?.label || item.name}
                        </span>
                      </div>
                      {item.value && (
                        <span className='text-foreground font-mono font-medium tabular-nums'>
                          {item.value.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);
ChartTooltipContent.displayName = 'ChartTooltip';

const ChartLegend = RechartsPrimitive.Legend;

const ChartLegendContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> &
    DefaultLegendContentProps & {
      hideIcon?: boolean;
      nameKey?: string;
    }
>(
  (
    { className, hideIcon = false, payload, verticalAlign = 'bottom', nameKey },
    ref
  ) => {
    const { config } = useChart();

    if (!payload?.length) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-center gap-4',
          verticalAlign === 'top' ? 'pb-3' : 'pt-3',
          className
        )}>
        {payload.map((item) => {
          const key = `${nameKey || item.dataKey || 'value'}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);

          return (
            <div
              key={key}
              className={cn(
                '[&>svg]:text-muted-foreground flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3'
              )}>
              {itemConfig?.icon && !hideIcon ? (
                <itemConfig.icon />
              ) : (
                <div
                  className='size-2 shrink-0 rounded-[2px]'
                  style={{
                    backgroundColor: `var(--color-${key}, ${item.color})`
                  }}
                />
              )}
              {itemConfig?.label}
            </div>
          );
        })}
      </div>
    );
  }
);
ChartLegendContent.displayName = 'ChartLegend';

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: unknown,
  key: string
) {
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }

  const payloadPayload =
    'payload' in payload &&
    typeof payload.payload === 'object' &&
    payload.payload !== null
      ? payload.payload
      : undefined;

  let configLabelKey: string = key;

  if (
    key in payload &&
    typeof payload[key as keyof typeof payload] === 'string'
  ) {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === 'string'
  ) {
    configLabelKey = payloadPayload[
      key as keyof typeof payloadPayload
    ] as string;
  }

  return configLabelKey in config
    ? config[configLabelKey]
    : config[key as keyof typeof config];
}

export {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent
};
